import React, { useEffect, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import { Button, InputField, RadioGroup, Select, SelectOption } from '@freelancelabs/components';

import { ListSkeleton } from '../../../components/Skeletons';
import { DataScope } from '../../../graphql/__generated__/schema';
import { AccountActivitiesDocument } from '../../../graphql/__generated__/types';
import ActivityPicker from './ActivityPicker';
import { DraftAttachment, initialActivityIds } from './draft';
import { OnboardingRole, findRole, roleRefusal } from './grantableRoles';

/** La même valeur que les deux recherches du portail : deux champs qui réagissent à des
 *  vitesses visiblement différentes se lisent comme deux contrôles. */
const SEARCH_DEBOUNCE_MS = 300;

interface Props {
    attachment: DraftAttachment;
    accountName: string;
    /** The address entered at step 1. It is what designates the person: the journey resolves no contact. */
    email: string;
    roles: OnboardingRole[];
    onChange: (patch: Partial<DraftAttachment>) => void;
    onRemove: () => void;
    /** Back to step 1. Offered on the one empty case where re-reading the address is the gesture. */
    onEditIdentity: () => void;
}

/**
 * One account, and what the person will carry on it: a role and a perimeter.
 *
 * The markets are only fetched when the perimeter is restricted. Loading them for
 * every added account would query accounts nobody is going to restrict, and the
 * list can be long.
 *
 * ── Le pré-cochage (PCG-63)
 *
 * Restricting the perimeter starts from the markets that carry **this person's own
 * business**, ticked and untickable. The reason to trust it is that the attachment
 * comes from the source: the suggestion reflects it instead of inventing one.
 *
 * A ticked box is a perimeter nobody decided, so three things hold it in place, and
 * none of them is decoration:
 *
 * - **it says it is one, and why**: a badge carrying the number of needs sits on
 *   every suggested market, so a box ticked by the query never reads like a box
 *   ticked by hand;
 * - **it is an initialisation, not a constraint**: applied once per (account ×
 *   address), so unticking sticks and nothing re-applies behind the operator;
 * - **the recap lists what is granted**, markets named one by one, because the
 *   administrator validates a screen and not each box.
 */
const AttachmentRow = ({ attachment, accountName, email, roles, onChange, onRemove, onEditIdentity }: Props) => {
    const restricted = attachment.dataScope === DataScope.Restricted;

    /**
     * La recherche, cote SERVEUR : elle porte sur les deux listes a la fois, comme la
     * recherche unique de la maquette. Un filtre qui ne toucherait qu'une des deux ferait
     * croire que l'autre n'a rien qui corresponde.
     */
    const [search, setSearch] = useState('');
    /**
     * LA VALEUR QUI PART A L'API, ET ELLE EST EN RETARD SUR CELLE QU'ON TAPE.
     *
     * Sans ce délai, chaque touche envoyait une requête, et une requête en vol rend
     * `data` indéfini : le sélecteur disparaissait sous les doigts de l'opérateur, qui
     * voyait deux lettres sur quatre. Le champ est resté monté (voir plus bas), mais une
     * requête par touche reste une requête par touche.
     *
     * 300 ms, la même valeur que les deux recherches du portail : deux champs qui
     * réagissent à des vitesses visiblement différentes se lisent comme deux contrôles.
     */
    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    /**
     * UNE SEULE REQUETE la ou il y en avait deux, et ce n'est pas qu'une economie.
     *
     * L'offre et la suggestion etaient separees parce que les marches d'un compte et les
     * marches d'une personne sont deux faits. Ici c'est le MEME fait : la liste porte son
     * propre pre-cochage, ligne par ligne. Deux requetes en rendraient deux versions, libres
     * de se contredire le temps d'un chargement.
     *
     * Attention, le compte voyage en ARGUMENT et jamais en `x-organization-id` : cet ecran
     * configure des comptes qui ne sont pas celui de l'acteur, et l'en-tete ferait echouer
     * chaque appel sur un refus qui se lit comme un bug d'autorisation.
     */
    const {
        data: activityData,
        previousData: previousActivityData,
        loading: activitiesLoading,
        error: activitiesError,
        refetch: refetchActivities,
    } = useQuery(AccountActivitiesDocument, {
        variables: { organizationId: attachment.organizationId, email, search: debouncedSearch },
        skip: !restricted,
    });
    /**
     * LA LISTE PRECEDENTE TIENT PENDANT QUE LA SUIVANTE ARRIVE, mais seulement une fois
     * le pré-cochage appliqué à CETTE adresse.
     *
     * La retenir toujours serait le défaut que ce fichier passe son temps à éviter :
     * l'adresse changée, `previousData` porte les affaires de la personne d'AVANT, et
     * les cases affichées seraient encore les siennes. Tant que l'instantané ne nomme
     * pas l'adresse courante, on attend donc, comme avant.
     *
     * Une fois qu'il la nomme, ce qui est coché appartient à cette adresse et la seule
     * raison de recharger est la recherche : garder la liste sous les yeux vaut mieux
     * que la remplacer par « Chargement des activités… » à chaque mot tapé.
     */
    const selectedCount = attachment.needIds.length + attachment.serviceIds.length;
    const suggestionApplied = attachment.suggestion?.email === email;
    const activities =
        activityData?.accountActivities ??
        (suggestionApplied ? (previousActivityData?.accountActivities ?? null) : null);

    /**
     * Applied once per (account × address), and that is the whole safety of it.
     *
     * `loading` guards the moment the address changes: Apollo keeps the previous
     * answer on screen until the new one lands, and applying it then would tick the
     * former person's markets under the new address: the exact accident the
     * bookkeeping exists to prevent.
     *
     * Nothing here re-applies: once the snapshot names the current address the guard
     * is false forever after, so unticking a box is final. A **failed** query writes
     * no snapshot either, so retrying it still initialises.
     */
    useEffect(() => {
        if (!restricted || activitiesLoading || !activities) {
            return;
        }
        if (attachment.suggestion?.email === email) {
            return;
        }
        /**
         * Attention, LE PRE-COCHAGE NE SE FAIT QUE SUR LA LISTE ENTIERE, jamais sur une liste
         * filtree : appliquee sous une recherche, la suggestion ne cocherait que ce que la
         * recherche laisse voir, et l'instantane dirait pourtant qu'elle a ete appliquee. Les
         * lignes cachees seraient alors perdues sans que rien ne le signale, et revenir a la
         * liste complete ne les ramenerait pas.
         */
        if (search.trim().length > 0 || debouncedSearch.trim().length > 0) {
            return;
        }
        const suggestedNeeds = activities.needs.filter((n) => n.linkedToContact).map((n) => n.id as string);
        const suggestedServices = activities.marketGroups
            .flatMap((group) => group.services)
            .filter((s) => s.linkedToContact)
            .map((s) => s.id as string);
        onChange({
            ...initialActivityIds(attachment, suggestedNeeds, suggestedServices),
            suggestion: {
                email,
                // Ce qui est range est ce que le pre-cochage a AJOUTE, jamais ce que la source
                // designe : c'est ce qui rend le changement d'adresse sur. Voir `draft.ts`.
                needIds: suggestedNeeds,
                serviceIds: suggestedServices,
                matchedContactCount: activities.matchedContactCount,
            },
        });
    }, [restricted, activitiesLoading, activities, email, search, debouncedSearch, attachment, onChange]);

    /**
     * Every role of the scope, and the refused ones **disabled rather than removed**.
     *
     * Removing them would be the easier render and the wrong screen: an administrator
     * offered two roles out of seven reads an incomplete referential and goes looking
     * for the missing five. « Vous ne pouvez pas accorder ce rôle » is not « ce rôle
     * n'existe pas », and only the first one is true. Same lesson as the empty picker.
     *
     * The API is what makes it possible (it deliberately stops filtering) and it is
     * the same `can_grant_role()` that refuses at the send, so this list and the
     * mutation cannot contradict each other.
     */
    const roleOptions: SelectOption<string>[] = roles
        .filter((role) => role.id)
        .map((role) => ({
            label: role.name ?? '',
            value: role.id as string,
            disabled: !role.grantableByViewer,
            disabledReason: role.grantableByViewer ? undefined : roleRefusal(role.notGrantableReason).tag,
        }));

    /**
     * The role already picked on this account, when it has stopped being grantable.
     *
     * It stays selected and stays named: clearing it would tidy the screen and lose
     * the fact: the operator would find an empty picker where they had chosen, with
     * nothing saying what happened or that anything did.
     */
    const chosen = findRole(roles, attachment.roleId);
    const chosenRefusal = chosen?.grantableByViewer === false ? roleRefusal(chosen.notGrantableReason) : null;

    const toggleNeed = (needId: string, checked: boolean) =>
        onChange({
            needIds: checked ? [...attachment.needIds, needId] : attachment.needIds.filter((id) => id !== needId),
        });

    const toggleService = (serviceId: string, checked: boolean) =>
        onChange({
            serviceIds: checked
                ? [...attachment.serviceIds, serviceId]
                : attachment.serviceIds.filter((id) => id !== serviceId),
        });

    /**
     * Cocher un marche coche ses PRESTATIONS, et c'est tout ce qui est enregistre : le marche
     * lui-meme ne l'est jamais. C'est le dessin de la maquette, et c'est ce qui empeche le
     * defaut que ce lot corrige de revenir par la porte de l'ecran.
     *
     * Attention, IL NE COCHE QUE CE QUI EST A L'ECRAN. Sous une recherche, le groupe ne porte
     * que les prestations retenues : cocher le cadre coche celles-la, pas les autres. C'est le
     * comportement attendu d'une case qui reprend ce qu'elle a sous elle, et le compteur de la
     * selection dit ensuite combien sont retenues en tout.
     */
    const toggleMarket = (ids: string[], checked: boolean) =>
        onChange({
            serviceIds: checked
                ? [...new Set([...attachment.serviceIds, ...ids])]
                : attachment.serviceIds.filter((id) => !ids.includes(id)),
        });

    /**
     * The applied suggestion, or null while none has been. Everything the row says about the
     * pre-ticking is read from **here** and not from the query, so the badges and the ticks
     * can never tell two different stories: they were written in the same gesture.
     */
    const applied = attachment.suggestion?.email === email ? attachment.suggestion : null;
    const suggestedCount = (applied?.needIds.length ?? 0) + (applied?.serviceIds.length ?? 0);
    const matchedContactCount = applied?.matchedContactCount ?? 0;

    /**
     * POURQUOI RIEN N'EST PRÉ-COCHÉ, et c'est l'issue ORDINAIRE : la plupart des personnes
     * qu'on onboarde ne sont pas des contacts ingérés.
     *
     * **Attention, LES QUATRE CAS DE L'ANCIENNE VERSION ONT FONDU EN UN, et c'est une perte
     * assumée.** `suggestedMarkets` séparait « aucun contact pour cette adresse », « contact
     * sans affaire », « affaires hors des marchés du compte » et « compte sans marché », parce
     * que les quatre appelaient des gestes différents. `accountActivities` ne rend pas de
     * motif : la liste porte son propre pré-cochage, ligne par ligne, et l'absence de ligne
     * cochée est le seul fait qu'on puisse en tirer.
     *
     * Les deux gestes qui restaient utiles sont gardés : vérifier l'adresse, et cocher à la
     * main. Le troisième, « ajoutez l'autre compte du groupe », est parti avec le motif qui le
     * portait, et il faudra le rendre à l'API si le produit le redemande.
     */
    const nothingPreTicked = Boolean(applied) && suggestedCount === 0;

    return (
        <li className="rounded-lg border border-border-norm p-4">
            <div className="flex flex-wrap items-center gap-3">
                <h3 className="text-sm font-semibold text-text-900">{accountName}</h3>
                <Button className="ml-auto" size="small" category="weak" onClick={onRemove}>
                    {c('Onboarding action').t`Remove this account`}
                </Button>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-4">
                <div>
                    <p className="mb-2 text-sm font-medium text-text-900">{c('Onboarding field').t`Role`}</p>
                    <Select
                        options={roleOptions}
                        value={roleOptions.find((option) => option.value === attachment.roleId)}
                        onChange={(option) => onChange({ roleId: option.value })}
                        placeholder={c('Onboarding field').t`Pick a role`}
                        className="max-w-xs"
                        error={chosenRefusal?.tag}
                    />
                    {/* The one thing that is true of *this* account and not of the step:
                        a pick that has stopped being valid. The rule behind the refusal
                        is said once above the list: repeating it under every account
                        would bury the picker it annotates. */}
                    {chosenRefusal && (
                        <p className="mt-2 text-sm text-danger-600" role="alert">
                            {c('Onboarding role refusal')
                                .t`This role stays picked but you can no longer grant it — ${chosenRefusal.tag}. The invitation would be refused when sent: pick another one.`}
                        </p>
                    )}
                    <p className="mt-2 text-xs text-text-600">
                        {c('Onboarding hint').t`The role carries what the person may do on this account.`}
                    </p>
                </div>

                <div className="lg:col-span-3">
                    <p className="mb-2 text-sm font-medium text-text-900">{c('Onboarding field').t`Perimeter`}</p>
                    <RadioGroup
                        name={`scope-${attachment.organizationId}`}
                        value={attachment.dataScope}
                        onValue={(value: DataScope) =>
                            // Leaving the scope drops the market selection: keeping it would
                            // send markets the API ignores, and show a perimeter nobody set.
                            //
                            // The snapshot goes with it. It records that the selection was
                            // initialised, so keeping it across a reset selection would leave
                            // the two disagreeing: coming back to « certains marchés » would
                            // show an empty list, which reads as « cette personne ne porte
                            // aucune affaire » rather than as a selection that was cleared.
                            onChange({ dataScope: value, needIds: [], serviceIds: [], suggestion: null })
                        }
                        category="card"
                        options={[
                            {
                                value: DataScope.All,
                                label: c('Onboarding perimeter').t`The whole activity of the accounts`,
                                // Le périmètre entier n'est pas « tous les marchés », c'est
                                // l'ABSENCE de restriction personnelle, donc il ne se périme pas.
                                // La pastille « Recommandé » de la maquette est retirée : on
                                // n'oriente pas le choix de périmètre depuis l'écran.
                                description: c('Onboarding perimeter')
                                    .t`An evolving perimeter: a need or a service attached to these accounts later becomes visible on its own.`,
                            },
                            {
                                value: DataScope.Restricted,
                                label: c('Onboarding perimeter').t`Pick some activities`,
                                // ATTENTION, LA MAQUETTE PROMET ICI CE QUE L'API NE TIENT PAS.
                                // Elle écrit que les activités désignées par Salesforce « le
                                // restent automatiquement, y compris pour les futurs éléments
                                // rattachés au contact ». Faux chez nous : `membership_need` et
                                // `membership_service` sont une liste d'identifiants FIGÉE à
                                // l'invitation, et la source pré-coche sans jamais réviser.
                                // Recopier la phrase ferait accorder un périmètre qui ne
                                // grandira pas à quelqu'un à qui on a promis le contraire.
                                description: c('Onboarding perimeter')
                                    .t`A perimeter frozen on what is ticked below. An activity attached to this person later stays invisible until someone comes back and adds it.`,
                            },
                        ]}
                    />
                </div>
            </div>

            {restricted && (
                <div className="mt-4 border-t border-border-norm pt-4">
                    {/*
                        LE CHAMP DE RECHERCHE VIT ICI ET NON DANS LE SELECTEUR, et c'est une
                        correction et non un rangement.

                        Il etait rendu par `ActivityPicker`, lui-meme conditionne a la presence
                        d'une liste. Une requete en vol rend `data` indefini : le selecteur
                        disparaissait donc A CHAQUE TOUCHE, emportant le champ, son contenu et le
                        curseur. On tapait quatre lettres, il en restait deux.

                        La regle qui en sort : UN CONTROLE QUI FILTRE NE SE MONTE JAMAIS SOUS CE
                        QU'IL FILTRE. Son etat vit au-dessus, il survit au chargement, a l'echec
                        et au vide, ce qui est aussi la seule facon de repartir d'une recherche
                        qui ne rend rien.

                        Le compteur le suit pour la meme raison : il porte ce qui est COCHE, un
                        fait du brouillon et non de la liste, donc il n'a aucune raison de
                        clignoter quand la liste voyage.
                    */}
                    <div className="mb-3 flex flex-wrap items-center gap-3">
                        <div className="grow">
                            <InputField
                                id={`activity-search-${attachment.organizationId}`}
                                label={c('Onboarding activity').t`Search an activity`}
                                type="search"
                                value={search}
                                onValue={setSearch}
                            />
                        </div>
                        <span className="shrink-0 rounded-full border border-border-norm px-3 py-1 text-xs text-text-600">
                            {selectedCount === 0
                                ? c('Onboarding activity').t`No activity selected`
                                : c('Onboarding activity').t`${selectedCount} selected`}
                        </span>
                    </div>

                    {/* La liste est attendue avant de paraitre. Rendre les cases decochees puis
                        les cocher un instant plus tard montrerait a l'operateur un perimetre qui
                        n'est pas celui qu'il s'apprete a accorder. */}
                    {activitiesLoading && !activities && <ListSkeleton rows={6} withControl />}

                    {activitiesError && (
                        <div className="mb-3 flex flex-wrap items-center gap-3">
                            {/* Un echec ne doit pas prendre le selecteur en otage : il n'y a rien a
                                cocher tant que la liste n'a pas repondu, et le taire laisserait
                                « rien de pre-coche » passer pour « cette personne n'a rien ». */}
                            <p className="text-xs text-danger-700" role="alert">
                                {c('Onboarding')
                                    .t`The activities of this account could not be read, so nothing is listed and nothing is pre-ticked.`}
                            </p>
                            <Button category="weak" size="small" onClick={() => refetchActivities()}>
                                {c('Onboarding action').t`Try again`}
                            </Button>
                        </div>
                    )}

                    {nothingPreTicked && (
                        <div className="mb-3">
                            <Button category="weak" size="small" onClick={onEditIdentity}>
                                {c('Onboarding action').t`Check the address`}
                            </Button>
                        </div>
                    )}

                    {/* Une adresse portee par plusieurs contacts melange les affaires de deux
                        personnes : l'ecran le dit plutot que de laisser passer la suggestion pour
                        le perimetre d'une seule. */}
                    {matchedContactCount > 1 && (
                        <p className="mb-3 text-xs text-danger-700" role="alert">
                            {c('Onboarding suggestion')
                                .t`This address is carried by several ingested contacts, so what is pre-ticked mixes their activities. Check each line before sending.`}
                        </p>
                    )}

                    {activities && (
                        <ActivityPicker
                            activities={activities}
                            needIds={attachment.needIds}
                            serviceIds={attachment.serviceIds}
                            onToggleNeed={toggleNeed}
                            onToggleService={toggleService}
                            onToggleMarket={toggleMarket}
                        />
                    )}
                </div>
            )}
        </li>
    );
};

export default AttachmentRow;
